import getConfig from 'next/config';
import { notification } from 'antd';

import { gwPost } from '../actionsFactory/gwRequest';
import API_PATH from '../actionsFactory/apiPath';
import createBaseActions, { handleGwData, setPostBody } from '../actionsFactory';
import actionTypes from '../actionsFactory/types';
import bog from '../../utils/moreBog';

const {
  publicRuntimeConfig: {
    enableMargin,
  },
} = getConfig();

const INIT_PAGE = 1;
const INIT_ROWS = 10;
const ERROR_NOTIFICATION_SHOW_SECS = 10;
const {
  PLACE_ORDER_START,
  PLACE_ORDER_SUCCESS,
  PLACE_ORDER_FAILED,
  DELETE_ORDER_START,
  DELETE_ORDER_SUCCESS,
  DELETE_ORDER_FAILED,
  SET_ORDERFORM_PRICE_AMOUNT,
  FETCH_USER_TRADES_DATA,
  SET_USER_TRADES_DATA,
  SET_USER_TRADES_DATA_WITH_BEFORE,
  FETCH_USER_TRADES_DATA_ERROR,
  FETCH_OPEN_ORDERS_DATA,
  FETCH_OPEN_ORDERS_ERROR,
  SET_OPEN_ORDERS_DATA,
  FETCH_TRADE_ORDERS_DATA,
  FETCH_TRADE_ORDERS_ERROR,
  SET_TRADE_ORDERS_DATA,
  SET_TRADE_ORDERS_DATA_WITH_BEFORE,
  SET_MARGIN_TRADE_ORDERS_DATA_WITH_BEFORE,
  RESET_TRADE_ORDERS_PAGINATION,
  RESET_USER_TRADES_PAGINATION,
  SET_MARGIN_OPEN_ORDERS_DATA,
  SET_MARGIN_TRADE_ORDERS_DATA,
} = actionTypes;

const dispatchNoPayload = type => () => dispatch => dispatch({ type });

const placeOrderStart = createBaseActions(PLACE_ORDER_START, 'reason');
const setPlaceOrderSuccess = createBaseActions(PLACE_ORDER_SUCCESS, 'result');
const placeOrderError = createBaseActions(PLACE_ORDER_FAILED, 'reason');
const deleteOrderSuccess = createBaseActions(DELETE_ORDER_SUCCESS, 'result');
const deleteOrderError = createBaseActions(DELETE_ORDER_FAILED, 'reason');
const setUserTradesData = createBaseActions(SET_USER_TRADES_DATA, 'symbol', 'data', 'pagination');
const setUserTradesDataWithBefore = createBaseActions(SET_USER_TRADES_DATA_WITH_BEFORE,
  'data', 'pagination', 'isScrollToBottom');
const setOpenOrdersData = createBaseActions(SET_OPEN_ORDERS_DATA, 'data');
const setMarginOpenOrdersData = createBaseActions(SET_MARGIN_OPEN_ORDERS_DATA, 'data');
const setTradeOrdersData = createBaseActions(SET_TRADE_ORDERS_DATA, 'data', 'pagination');
const setMarginTradeOrdersData = createBaseActions(SET_MARGIN_TRADE_ORDERS_DATA, 'data', 'pagination');
const setTradeOrdersDataWithBefore = createBaseActions(SET_TRADE_ORDERS_DATA_WITH_BEFORE, 'data', 'pagination');
const setMarginTradeOrdersDataWithBefore = createBaseActions(SET_MARGIN_TRADE_ORDERS_DATA_WITH_BEFORE, 'data', 'pagination');

export const setPriceAndAmount = data => async dispatch => dispatch({
  type: SET_ORDERFORM_PRICE_AMOUNT,
  ...data,
});

const ACCOUNT_TYPE_ENUM = { MAIN: 'MAIN', MARGIN: 'MARGIN' };
export const MARGIN_ORDER_ENUM = {
  NO_SIDE_EFFECT: 'NO_SIDE_EFFECT',
  MARGIN_BUY: 'MARGIN_BUY',
  AUTO_REPAY: 'AUTO_REPAY',
};

export const placeOrder = (formData, msgData, orderType) => async (dispatch, getState) => {
  const { isMarginPair, marginStatus } = getState();
  dispatch(placeOrderStart());
  const margin = enableMargin && isMarginPair && marginStatus; // margin开启选项，是否为margin交易对，margin tab切换状态
  let requestApi = API_PATH.ORDER_API_PATH;
  if (orderType === 'oco') requestApi = API_PATH.OCO_ORDER_API_PATH;
  if (margin) requestApi = API_PATH.MARGIN_ORDER_PLACE;
  try {
    if (typeof formData !== 'object') throw new Error('Expected an object');
    handleGwData(await gwPost(requestApi, setPostBody(formData)));
    bog.info('Order Placed', formData);
    notification.success({
      message: msgData.successTitle || 'Order Placed',
      description: msgData.successText,
    });
  } catch (error) {
    dispatch(placeOrderError(error));
    throw error;
  }
};

export const placeOrderSuccess = () => async (dispatch) => {
  dispatch(setPlaceOrderSuccess());
};

export const deleteOrder = (formData, forceMargin) => async (dispatch, getState) => {
  const { isMarginPair, marginStatus } = getState();
  let margin = (typeof forceMargin === 'undefined') ? marginStatus : forceMargin;
  if (!enableMargin || !isMarginPair) margin = false;
  const accountType = margin ? ACCOUNT_TYPE_ENUM.MARGIN : ACCOUNT_TYPE_ENUM.MAIN;
  dispatch({ type: DELETE_ORDER_START, formData });
  const {
    locale, localeData,
  } = getState();
  const i18n = localeData[locale];
  try {
    if (typeof formData !== 'object') throw new Error('Expected an object');

    let api;
    let params = {};
    const { type, orderId, symbol } = formData;
    if (type && !margin) {
      api = API_PATH.DELETE_ALL_ORDER_API_PATH;
      params = { type };
      if (type === 'ALL') params = null;
    } else {
      api = margin ? API_PATH.MARGIN_ORDER_DELETE_ORDER : API_PATH.DELETE_ORDER_API_PATH;
      const isArray = Array.isArray(orderId);
      params = {
        orderIds: isArray ? orderId : [orderId],
        symbols: isArray ? symbol : [symbol], // margin cancel all
      };
    }

    if (!margin) params = { ...params, accountType };
    let result = await gwPost(api, { locale, ...setPostBody(params) });
    result = handleGwData(result);

    bog.info('Order Deleted', params);
    notification.success({
      message: i18n.cancleSuccess || 'Order Deleted',
      description: i18n.cancelOrderSucceed,
    });
    dispatch(deleteOrderSuccess(result));
    return result;
  } catch (error) {
    bog.error(`Order error: ${error ? error && error.message : ''}`);
    notification.error({
      message: i18n.cancelOrderFail || 'Order Error',
      description: error ? error && error.message : '',
      duration: ERROR_NOTIFICATION_SHOW_SECS,
    });
    dispatch(deleteOrderError(error));
    return {};
  }
};

export const fetchMarginOrderBorrowable = async params => {
  let result;
  try {
    result = await gwPost(API_PATH.MARGIN_ORDER_PLACE_ASSET_BORROWABLE, setPostBody(params));
    result = handleGwData(result);
  } catch (error) {
    result = {};
  }
  return result;
};

export const fetchUserTradesData = (postData, locale, isScrollToBottom) => async (dispatch, getState) => {
  const { marginStatus, isLoggedIn } = getState();
  if (!isLoggedIn) return;
  dispatch({ type: FETCH_USER_TRADES_DATA, postData });
  if (!postData.startTime || !postData.endTime) {
    throw new Error('fetchUserTradesData: `startTime` and `endTime` were expected in request data');
  }
  try {
    if (postData && !postData.page) {
      postData = {
        page: INIT_PAGE,
        rows: INIT_ROWS,
      };
    }
    const userTrades = await gwPost(API_PATH.USER_TRADES_API_PATH, {
      locale,
      ...setPostBody({ ...postData, accountType: marginStatus ? ACCOUNT_TYPE_ENUM.MARGIN : ACCOUNT_TYPE_ENUM.MAIN }),
    });
    const pagination = Object.assign(
      postData,
      { pages: Math.ceil(userTrades.total / postData.rows) }
    );
    isScrollToBottom || !pagination.symbol ?
      dispatch(setUserTradesDataWithBefore(userTrades.data, pagination, isScrollToBottom))
      : dispatch(setUserTradesData(postData.symbol, userTrades.data, pagination));
  } catch (error) {
    dispatch({ type: FETCH_USER_TRADES_DATA_ERROR, error });
  }
};

export const fetchOpenOrders = (locale, forceMargin) => async (dispatch, getState) => {
  const { marginStatus } = getState();
  let shouldDispatchMargin = false;
  forceMargin !== undefined ? shouldDispatchMargin = forceMargin : shouldDispatchMargin = marginStatus;
  dispatch({ type: FETCH_OPEN_ORDERS_DATA });
  const margin = shouldDispatchMargin ? { accountType: 'MARGIN' } : {};
  try {
    let openOrders = await gwPost(API_PATH.OPEN_ORDERS_API_PATH, {
      locale,
      ...setPostBody(margin),
    });
    openOrders = handleGwData(openOrders);
    shouldDispatchMargin ? dispatch(setMarginOpenOrdersData(openOrders || [])) : dispatch(setOpenOrdersData(openOrders || []));
  } catch (error) {
    dispatch({ type: FETCH_OPEN_ORDERS_ERROR, error });
  }
};

export const fetchTradeOrders = (postData, locale, isScrollToBottom, forceMargin) => async (dispatch, getState) => {
  const { marginStatus } = getState();
  const shouldDispatchMargin = (typeof forceMargin === 'undefined') ? marginStatus : forceMargin;
  const margin = shouldDispatchMargin ? { accountType: 'MARGIN' } : {};
  dispatch({ type: FETCH_TRADE_ORDERS_DATA });
  if (!postData.startTime || !postData.endTime) {
    throw new Error('fetchTradeOrders: `startTime` and `endTime` were expected in request data');
  }
  try {
    if (postData && !postData.page) {
      postData = {
        page: INIT_PAGE,
        rows: INIT_ROWS,
      };
    }
    const tradeOrders = await gwPost(API_PATH.TRADE_ORDERS_API_PATH, {
      locale,
      ...setPostBody({ ...postData, ...margin }),
    });
    const pagination = Object.assign(postData, {
      pages: Math.ceil(tradeOrders.total / postData.rows),
    });
    if (isScrollToBottom) {
      shouldDispatchMargin ?
        dispatch(setMarginTradeOrdersDataWithBefore(tradeOrders.data, pagination))
        : dispatch(setTradeOrdersDataWithBefore(tradeOrders.data, pagination));
    } else {
      shouldDispatchMargin ? dispatch(setMarginTradeOrdersData(tradeOrders.data, pagination)) : dispatch(setTradeOrdersData(tradeOrders.data, pagination));
    }
  } catch (error) {
    dispatch({ type: FETCH_TRADE_ORDERS_ERROR, error });
  }
};

export const resetTradeOrdersPagination = dispatchNoPayload(RESET_TRADE_ORDERS_PAGINATION);
export const resetUserTradesPagination = dispatchNoPayload(RESET_USER_TRADES_PAGINATION);

export default {};
